<template>
  <GamePane product="tai-xiu" :wide="wide">
    <template #title>
      <div v-if="table" class="tai-xiu-pane__identity">
        <strong>{{ table.code }}</strong>
        <span v-if="table.name" class="tai-xiu-pane__name">· {{ table.name }}</span>
      </div>
    </template>

    <template #video>
      <TaiXiuFullVideo :arena-id="arenaId" />
    </template>

    <!--
      The same bar the mobile full board shows under its video -- table code and
      round, the result dice in their pill, the countdown. Shared component, so
      the two clients cannot drift apart again.
    -->
    <template v-if="table" #info>
      <TaiXiuBoardHeader :table="table" :is-rolling="isRolling" float-result />
    </template>

    <!--
      Single view: the roads move out of the board into the left column, beside
      the betting instead of under it. `hideCharts` keeps the board from drawing
      a second copy; in the split view neither is used and the board is whole.
    -->
    <template v-if="wide && table" #aside>
      <BetHistoryCharts
        class="tai-xiu-pane__roads"
        :recent-results="table.recentResults"
        :recent-dice="table.recentDice"
        :recent-rounds="table.recentRounds"
        :code="table.code"
      />
    </template>

    <!--
      Split view: the rail is its own band under the video. Single view: it goes
      inside the board, under the markets and above the chip row, as the design
      shows -- the same place the cockfight pane puts its arena rail.
    -->
    <template v-if="!wide" #selector>
      <ArenaChips :items="arenaItems" @select="$emit('select-arena', $event)" />
    </template>

    <BetBoard
      v-if="table"
      class="tai-xiu-pane__board"
      :class="{ 'tai-xiu-pane__board--wide': wide }"
      full
      :hide-charts="wide"
      v-bind="boardProps"
      v-on="boardListeners"
    >
      <template v-if="wide" #under-board>
        <ArenaChips
          class="tai-xiu-pane__rail"
          :items="arenaItems"
          @select="$emit('select-arena', $event)"
        />
      </template>
    </BetBoard>
  </GamePane>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import GamePane from '@/components/desktop/game-pane.vue';
import TaiXiuFullVideo from '@/components/tai-xiu/tai-xiu-full-video.vue';
import ArenaChips from '@/components/desktop/arena-chips.vue';
import TaiXiuBoardHeader from '@/components/tai-xiu/tai-xiu-board-header.vue';
import BetBoard from '@/components/tai-xiu/bet-board.vue';
import BetHistoryCharts from '@/components/tai-xiu/bet-history-charts.vue';

/*
 * A desktop pane showing a Tài Xỉu table.
 *
 * Which table is decided by `desktop-client.vue` (either pane can hold any
 * arena of either product); this pane binds its store slip to that table, so
 * two panes on two tables keep separate draft and placed bets.
 *
 * `bet-board.vue` in `full` mode already draws the markets, the action bar and
 * the road charts, so the pane body is just the board -- the same component
 * the mobile full board gets in `pages/tai-xiu.vue`, fed from the pane's slip
 * instead of the root one.
 *
 * The video is the live stream: `tai-xiu-full-video.vue` takes the table's
 * `arenaId` and requests the URL the socket publishes, so switching tables
 * re-points the stream.
 */
export default {
  name: 'TaiXiuPane',
  components: {
    GamePane,
    TaiXiuFullVideo,
    ArenaChips,
    TaiXiuBoardHeader,
    BetBoard,
    BetHistoryCharts,
  },
  props: {
    paneId: {
      type: String,
      required: true,
    },
    tableId: {
      type: String,
      default: '',
    },
    // The combined cockfight + Tài Xỉu rail, prepared by the desktop client.
    arenaItems: {
      type: Array,
      default: () => [],
    },
    // Single view: the pane fills the row in two columns.
    wide: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('taiXiu', ['paneSlip', 'paneTable', 'activeChip', 'isRolling', 'lastResult']),
    slip() {
      return this.paneSlip(this.paneId);
    },
    table() {
      return this.paneTable(this.paneId);
    },
    arenaId() {
      return (this.table && this.table.arenaId) || '';
    },
    boardProps() {
      return {
        table: this.table,
        // The pane's own chip, so two Tài Xỉu panes do not share one.
        activeChip: this.slip.activeChip || this.activeChip,
        draftBets: this.slip.draftBets,
        placedBets: this.slip.placedBets,
        canUndo: this.slip.betHistory.length > 0,
        feedback: this.slip.feedback,
        isSubmitting: this.slip.isSubmitting,
        isRolling: this.isRolling,
        lastResult: this.lastResult,
      };
    },
    boardListeners() {
      return {
        bet: (selection) => this.addPaneBet({ paneId: this.paneId, selection }),
        'select-chip': (amount) => this.selectPaneChip({ paneId: this.paneId, amount }),
        undo: () => this.undoPaneBet(this.paneId),
        clear: () => this.clearPaneBets(this.paneId),
        submit: () => this.submitPaneBets(this.paneId),
        // The desktop client vets it like a rail pick (not the other pane's arena).
        'select-table': (id) => this.$emit('select-arena', { product: 'tai-xiu', id }),
        'quick-play': this.setQuickPlay,
      };
    },
  },
  watch: {
    tableId: {
      immediate: true,
      handler(tableId) {
        this.setPaneTable({ paneId: this.paneId, tableId });
      },
    },
    /*
     * Each pane asks for its own table's chip set, so two panes on two tables
     * get two. The store-level sync already asks for every known table, but a
     * pane must not depend on having been mounted when that ran -- and the
     * action de-duplicates, so asking twice costs nothing.
     */
    arenaId: {
      immediate: true,
      handler(arenaId) {
        if (arenaId) this.getArenaBetChips(arenaId);
      },
    },
  },
  beforeDestroy() {
    // Free the slip: the pane may be switching to a cockfight arena.
    this.setPaneTable({ paneId: this.paneId, tableId: '' });
  },
  methods: {
    ...mapActions('taiXiu', [
      'setPaneTable',
      'setQuickPlay',
      'selectPaneChip',
      'addPaneBet',
      'undoPaneBet',
      'clearPaneBets',
      'submitPaneBets',
    ]),
    ...mapActions('general', ['getArenaBetChips']),
  },
};
</script>

<style lang="scss" scoped>
/*
 * v2.1 prototype (TxBoard). Split view: markets 212 ("md" type), chip row 48,
 * roads take the rest of the pane (~127). Single view, as the old UI: the
 * markets are the video's height (302 = 16:9 of the 536 column), the arena
 * tabs share the row with the video's info bar, and the chip row sits level
 * with the roads. Heights are fixed -- the frame is (desktop-client.vue).
 */
$markets-split-height: 212px;
$markets-wide-height: 302px; // the video's height; the board ends 16 above it
$markets-wide-gap: 16px;
$chip-row-wide-height: 64px;
$chip-row-height: 48px;
$strip-height: 15px;

.tai-xiu-pane__identity {
  display: flex;
  align-items: baseline;
  gap: 8px;
  color: #fff;
  font-size: 14px;

  strong {
    font-weight: 700;
  }
}

.tai-xiu-pane__name {
  color: $ga-text-muted;
  font-weight: 400;
}

// The video's corners are the pane's (game-pane.vue); the player's own 16 would
// show inside them.
::v-deep .tai-xiu-full-video {
  border-radius: 0;
}

/*
 * The board fills the pane body as a column: markets, (single view: tabs),
 * chip row, roads. Its own padding, radius and background are the mobile
 * card's, so they go. The class is repeated to outweigh the board's own rules
 * without `!important`.
 */
.tai-xiu-pane__board.tai-xiu-pane__board {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
}

/*
 * The mobile board pins its chip row to the bottom of its scroller; the desktop
 * column does not scroll, so it stays in flow.
 *
 * `relative`, not `static`: the row is the custom chip field's containing
 * block, and taking it out of the positioned flow let that field size itself
 * against the whole board instead -- a full-pane black sheet.
 */
.tai-xiu-pane__board ::v-deep .bet-board-actions--fancy {
  position: relative;
  background: transparent;
  box-shadow: none;
}

/*
 * The markets. The four groups of the prototype -- ALL PAY + main row, TRIPLE +
 * triples, the two sum rows, SINGLE/DOUBLE/TRIPLE + dice -- are 3px apart, and
 * every row is a fixed height. Doubled to outrank the board's own rules.
 */
.tai-xiu-pane__board.tai-xiu-pane__board ::v-deep {
  .full-board-body {
    flex: 0 0 auto;
    gap: 0;

    > * {
      flex-shrink: 0;
    }
  }

  .tx-info-bar {
    height: $strip-height;
    min-height: $strip-height;
    margin-top: 0;
    padding: 0;
    border-radius: 0;
    background: #1d292a;
    color: #e6cf87;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }

  .tx-triple-section,
  .full-sum-rows,
  .tx-info-bar--single {
    margin-top: 3px;
  }

  .tx-main-row {
    box-sizing: border-box;
  }

  .tx-main-row > button,
  .tx-odd-even > button {
    min-height: 0;
  }

  .tx-main-bet,
  .tx-odd-even__btn {
    padding: 0 2px;
  }

  .tx-main-bet > span:not(.sub) {
    font-weight: 700;
    line-height: 1;
  }

  /*
   * Secondary lines (BIG 11-17, ODD / EVEN, the sums' odds) dimmed under their
   * main label (PO feedback). Kept dim on a placed bet as well: the gold ring
   * marks that.
   */
  .tx-main-bet .sub,
  .tx-odd-even__btn small {
    margin-top: 1px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.82);
  }

  .tx-odd-even__btn span {
    font-weight: 700;
    line-height: 1;
  }

  .tx-triple-row,
  .full-sum-rows,
  .tx-dice-market .bet-dice-market__faces {
    border: 1px solid $ga-tx-cell-border;
    box-sizing: border-box;
  }

  .tx-triple-row .bet-triple-combo-btn {
    border-right-color: $ga-tx-cell-divider;
    background: $ga-tx-cell-bg;
  }

  .tx-triple-row button,
  .sum-grid button,
  .bet-dice-market button {
    min-height: 0;
    padding: 0;
  }

  .sum-grid {
    gap: 0;
  }

  .sum-grid button {
    justify-content: center;
    border: 0;
    border-left: 1px solid $ga-tx-cell-divider;
    border-top: 1px solid $ga-tx-cell-divider;
    border-radius: 0;
    background: $ga-tx-cell-bg;
  }

  .sum-grid strong {
    font-weight: 700;
    line-height: 1;
  }

  .sum-grid small {
    margin-top: 1px;
    font-size: 9px;
    font-weight: 600;
    line-height: 1.1;
    color: #c9d7ee;
  }

  .bet-dice-market__face {
    border-radius: 0;
    border-left: 1px solid $ga-tx-cell-divider;
  }
}

/* Split view: 212 = 15 + 55 | 3 + 15 + 21 | 3 + 60 | 3 + 15 + 21. "md" type. */
.tai-xiu-pane__board:not(.tai-xiu-pane__board--wide).tai-xiu-pane__board ::v-deep {
  .full-board-body {
    height: $markets-split-height;
  }

  .tx-main-row {
    height: 55px;
    min-height: 55px;
  }

  .tx-triple-row,
  .bet-dice-market__face {
    height: 21px;
    min-height: 21px;
  }

  .sum-grid button {
    height: 30px;
    min-height: 30px;
  }

  .tx-main-bet > span:not(.sub) {
    font-size: 22px;
  }

  .tx-main-bet .sub,
  .tx-odd-even__btn small {
    font-size: 9px;
  }

  .tx-odd-even__btn span {
    font-size: 14px;
  }

  .sum-grid strong {
    font-size: 13px;
  }

  .bet-dice-market__face img {
    width: 18px;
    height: 18px;
  }

  .tx-triple-row img {
    width: 13px;
    height: 13px;
  }
}

/*
 * Single view, as the old UI: 286 = 15 + 64 | 3 + 15 + 40 | 3 + 2 x 44 | 3 +
 * 15 + 40, ending 16 above the video's bottom so the arena tabs have room. The
 * tabs sit on the info bar's row, and the chip row comes 8 under them, level
 * with the roads.
 */
.tai-xiu-pane__board--wide.tai-xiu-pane__board--wide ::v-deep {
  .full-board-body {
    height: $markets-wide-height - $markets-wide-gap;
    margin-bottom: $markets-wide-gap;
  }

  .tx-info-bar {
    height: 15px;
    min-height: 15px;
  }

  .tx-main-row {
    height: 64px;
    min-height: 64px;
  }

  .tx-triple-row,
  .bet-dice-market__face {
    height: 40px;
    min-height: 40px;
  }

  .sum-grid button {
    height: 44px;
    min-height: 44px;
  }

  .tx-main-bet > span:not(.sub) {
    font-size: 20px;
  }

  .tx-main-bet .sub,
  .tx-odd-even__btn small {
    font-size: 10px;
  }

  .tx-odd-even__btn span {
    font-size: 15px;
  }

  .sum-grid strong {
    font-size: 14px;
  }

  .sum-grid small {
    font-size: 9px;
  }

  .bet-dice-market__face img {
    width: 20px;
    height: 20px;
  }

  .tx-triple-row img {
    width: 15px;
    height: 15px;
  }
}

.tai-xiu-pane__board--wide.tai-xiu-pane__board--wide {
  // Stacks at its own height in the right column; nothing below grows.
  flex: 0 0 auto;
  gap: 0;
}

// Single view: the arena tabs right under the markets, on the video info bar's
// row (34 tall, as that bar), then 8 to the chips -- the roads' 8 under the bar.
.tai-xiu-pane__rail {
  width: 100%;
  min-width: 0;
  height: 34px;
  min-height: 34px;
  box-sizing: border-box;
  margin: 0 0 8px;
  // Scroll rather than clip: the arena list is longer than the column.
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

/*
 * The chip row (v2.1 ChipRow): 48 tall, Undo as a 32px button over an 8.5px
 * label, the 40px chips spread evenly, then an 84px BET. Doubled to outrank the
 * board's own rules without `!important`.
 */
.tai-xiu-pane__board.tai-xiu-pane__board ::v-deep {
  .bet-board-actions--fancy {
    flex: 0 0 $chip-row-height;
    height: $chip-row-height;
    grid-template-columns: 32px minmax(0, 1fr) 84px;
    column-gap: 8px;
  }

  .bet-board-actions__chips {
    justify-content: space-around;
    min-height: $chip-row-height;
    padding: 0;
  }

  .bet-chip-selector__option {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    min-height: 40px;
    padding: 0;
    box-sizing: border-box;
  }

  // The selected ring is drawn outside the chip, so the row must not clip it.
  .bet-chip-selector,
  .bet-chip-selector--fancy {
    overflow: visible;
  }

  .chip-value {
    font-size: 12px;
  }

  /*
   * Selected chip: a gap in the pane colour, then a white ring -- both outside
   * the chip, so the artwork is untouched. The custom chip marks itself active
   * from inside, so it is named separately.
   */
  .bet-chip-selector__option--active .bet-chip-selector__chip.chip-wrapper,
  .custom-chip.chip-wrapper.active {
    border-color: transparent;
    box-shadow:
      0 0 0 2px $ga-pane-bg,
      0 0 0 4px #fff;
  }

  .bet-board-actions__undo {
    width: 32px;
    height: auto;
    gap: 2px;

    img {
      width: 32px;
      height: 32px;
    }

    small {
      width: auto;
      height: auto;
      margin-top: 0;
      background: transparent;
      color: #8fb3e0;
      font-size: 8.5px;
      font-weight: 600;
      line-height: 1;
      letter-spacing: 0.04em;
    }
  }

  .bet-board-actions--fancy .bet-board-actions__submit {
    width: 84px;
    height: 42px;
    min-height: 42px;
    font-size: 15px;
  }

  /*
   * The custom chip's amount field reserves a gutter for the chip and Save on
   * its right: 84 BET + 8 + a 40px chip + clearance.
   */
  .custom-chip-input {
    padding-right: 160px;
  }
}

/*
 * Placed-bet chips (bet-placed-chip.vue): a step up from the mobile board's in
 * the split view's main row, and larger again on the single view's cells.
 */
.tai-xiu-pane__board.tai-xiu-pane__board ::v-deep {
  .bet-placed-chip {
    width: 30px;
    height: 30px;
  }

  .bet-placed-chip--top-right.bet-placed-chip--overlap {
    width: 20px;
    height: 20px;
  }
}

// Single view: the old UI's larger chip row -- 54px chips, a 36px Undo and a
// 100px BET in a 64px row.
.tai-xiu-pane__board--wide.tai-xiu-pane__board--wide ::v-deep {
  .bet-board-actions--fancy {
    flex: 0 0 $chip-row-wide-height;
    height: $chip-row-wide-height;
    grid-template-columns: 40px minmax(0, 1fr) 100px;
    column-gap: 10px;
  }

  .bet-board-actions__chips {
    min-height: $chip-row-wide-height;
  }

  .bet-chip-selector__option {
    flex: 0 0 54px;
    width: 54px;
    height: 54px;
    min-height: 54px;
  }

  .chip-value {
    font-size: 15px;
  }

  .bet-board-actions__undo {
    width: 40px;

    img {
      width: 36px;
      height: 36px;
    }

    small {
      font-size: 10px;
    }
  }

  .bet-board-actions--fancy .bet-board-actions__submit {
    width: 100px;
    height: 46px;
    min-height: 46px;
    font-size: 17px;
  }

  .custom-chip-input {
    padding-right: 190px;
  }
}

.tai-xiu-pane__board--wide.tai-xiu-pane__board--wide ::v-deep {
  .bet-placed-chip {
    width: 42px;
    height: 42px;
  }

  .bet-placed-chip--top-right.bet-placed-chip--overlap {
    width: 28px;
    height: 28px;
  }

  // The triple row and the single/double/triple dice row are only 30px tall,
  // so a 28px chip 3px down ran past the zone's bottom edge (PO feedback).
  // Smaller there, and centred top to bottom.
  .tx-triple-row .bet-placed-chip--top-right.bet-placed-chip--overlap,
  .bet-dice-market .bet-placed-chip--top-right.bet-placed-chip--overlap {
    top: 50%;
    right: 4px;
    width: 22px;
    height: 22px;
    transform: translateY(-50%);

    .bet-placed-chip__value {
      &.f-12 {
        font-size: 11px;
      }

      &.f-11 {
        font-size: 10px;
      }

      &.f-9 {
        font-size: 8px;
      }

      &.f-8 {
        font-size: 7px;
      }
    }
  }

  .bet-placed-chip__value {
    &.f-12 {
      font-size: 16px;
    }

    &.f-11 {
      font-size: 14px;
    }

    &.f-9 {
      font-size: 12px;
    }

    &.f-8 {
      font-size: 10.5px;
    }
  }
}

/*
 * The roads (TxRoad): dark cards 6 apart. Split view: they take the rest of
 * the pane under the chip row. Their dots and cells are fixed px, so they are
 * sized with a zoom: 120 tall unzoomed, 0.95 -> ~114 of the ~127 left.
 */
.tai-xiu-pane__board:not(.tai-xiu-pane__board--wide) ::v-deep .bet-history-charts {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  margin-top: 0;
  zoom: 0.95;
  overflow: hidden;
}

::v-deep .bet-history-charts {
  gap: 6px;
}

// Single view: the roads under the video (aside, game-pane.vue), at the size
// the split view's leftover gives them (~124 on screen = 131 at 0.95), so the
// chart is the same size in both views.
.tai-xiu-pane__roads.tai-xiu-pane__roads {
  height: 131px;
  margin-top: 0;
  zoom: 0.95;
}

/*
 * The table bar under the video: the pane's 34px info band (game-pane.vue),
 * table and round left, the result dice in a 26px pill in the middle, on the
 * prototype's navy gradient with the bottom corners.
 */
::v-deep .tx-board-header {
  height: 34px;
  min-height: 34px;
  width: 100%;
  box-sizing: border-box;
  padding: 0 14px;
  border-radius: 0 0 10px 10px;
  background: $ga-info-bar-bg;

  .tx-board-header__identity strong {
    font-size: 14px;
    font-weight: 600;
  }

  .tx-board-header__dice {
    height: 26px;
    min-width: 84px;
    padding: 0 10px;
    box-sizing: border-box;
    border: 0;
    border-radius: 999px;
    background: $ga-dice-pill-bg;

    .tx-die {
      width: 20px;
      height: 20px;
    }
  }
}
</style>
